import { useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { ACTIVE_LANGS, LANG_PREFIX, LANG_LOCALE } from "../i18n/config";

const BASE_URL = "https://www.hestiadoors.com";

export const pageUrl = (lang, canonicalPath) =>
  `${BASE_URL}${LANG_PREFIX[lang]}${canonicalPath === "/" ? "/" : `${canonicalPath}/`}`;

const setMeta = (selector, attr, key, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setAlternate = (hreflang, href) => {
  let link = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

// canonicalPath is the English path, e.g. "/products" or "/"
const SEO = ({ title, description, canonicalPath, keywords, jsonLd, ogImage, ogType, noindex }) => {
  const { lang } = useLanguage();

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = lang;

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="robots"]', "name", "robots",
      noindex ? "noindex, nofollow" : "index, follow");
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", ogType || "website");
    setMeta('meta[property="twitter:title"]', "property", "twitter:title", title);
    setMeta('meta[property="twitter:description"]', "property", "twitter:description", description);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[property="og:locale"]', "property", "og:locale",
      LANG_LOCALE[lang] || "en_US");
    if (ogImage) {
      const img = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;
      setMeta('meta[property="og:image"]', "property", "og:image", img);
      setMeta('meta[property="twitter:image"]', "property", "twitter:image", img);
    }

    if (keywords) {
      setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }

    if (canonicalPath) {
      // Trailing slash matches what Apache serves (directory index -> /x/ redirect)
      const url = pageUrl(lang, canonicalPath);

      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", url);
      setMeta('meta[property="og:url"]', "property", "og:url", url);
      setMeta('meta[property="twitter:url"]', "property", "twitter:url", url);

      // hreflang only covers ACTIVE_LANGS — Danish appears automatically
      // once it is added to ACTIVE_LANGS in i18n/config.js.
      ACTIVE_LANGS.forEach((l) => setAlternate(l, pageUrl(l, canonicalPath)));
      setAlternate("x-default", pageUrl("en", canonicalPath));
    }

    let script = document.head.querySelector('script[data-page-jsonld]');
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-page-jsonld", "true");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, canonicalPath, keywords, jsonLd, ogImage, ogType, noindex, lang]);

  return null;
};

export default SEO;
